import axios from "axios";
import store from "../store";
import { oktaAuth } from "../config/okta";

class AuthService {
    constructor() {
        this.isRefreshing = false;
        this.failedQueue = [];
        this.setupAxiosInterceptors();
        this.adminUrl = `${import.meta.env.VITE_KHK_ADMIN_BASE_URL}/admin`;
    }

    processQueue(error, token = null) {
        this.failedQueue.forEach(({ resolve, reject }) => {
            if (error) {
                reject(error);
            } else {
                resolve(token);
            }
        });
        this.failedQueue = [];
    }

    setupAxiosInterceptors() {
        axios.interceptors.request.use(
            config => {
                const accessToken = store.state.access_token;
                if (accessToken) {
                    config.headers.Authorization = `Bearer ${accessToken}`;
                }
                // Brand switcher (#2122): tell management endpoints which brand the
                // admin has selected. Player-data endpoints ignore this and use the
                // backend-owned session brand, so a forged header cannot widen scope.
                const brandCode = store.state.selectedBrand?.code;
                if (brandCode) {
                    config.headers["X-Brand-Code"] = brandCode;
                }
                return config;
            },
            error => {
                return Promise.reject(error);
            }
        );

        axios.interceptors.response.use(
            response => {
                return response;
            },
            async error => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    if (this.isRefreshing) {
                        return new Promise((resolve, reject) => {
                            this.failedQueue.push({ resolve, reject });
                        })
                            .then(token => {
                                originalRequest.headers.Authorization = `Bearer ${token}`;
                                return axios(originalRequest);
                            })
                            .catch(err => {
                                return Promise.reject(err);
                            });
                    }

                    originalRequest._retry = true;
                    this.isRefreshing = true;

                    try {
                        const accessToken = await this.performTokenRefresh();
                        this.processQueue(null, accessToken);
                        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                        return axios(originalRequest);
                    } catch (refreshError) {
                        this.processQueue(refreshError, null);
                        store.commit("clearTokens");
                        if (window.location.pathname !== "/login") {
                            window.location.href = "/login";
                        }
                        return Promise.reject(refreshError);
                    } finally {
                        this.isRefreshing = false;
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    // Sync Okta SDK token state into Vuex store.
    // getTokensSync() returns a container: { accessToken: TokenObject, idToken: TokenObject, ... }
    // Each TokenObject has a same-named property holding the JWT string, hence the double dot:
    //   tokens.accessToken  → TokenObject
    //   tokens.accessToken.accessToken  → JWT string
    syncTokensFromOkta() {
        const tokens = oktaAuth.tokenManager.getTokensSync();
        if (tokens.accessToken) {
            store.commit("setAccessToken", tokens.accessToken.accessToken);
        }
        if (tokens.idToken) {
            store.commit("setIdToken", tokens.idToken.idToken);
        }
        if (tokens.refreshToken) {
            store.commit("setRefreshToken", tokens.refreshToken.refreshToken);
        }
    }

    // Listen for Okta SDK token renewal events and sync to Vuex.
    // The "renewed" callback receives the TokenObject directly (not the container),
    // so only one level of access is needed: newToken.accessToken → JWT string.
    startTokenSync() {
        oktaAuth.tokenManager.on("renewed", (_key, newToken) => {
            if (newToken.accessToken) {
                store.commit("setAccessToken", newToken.accessToken);
            } else if (newToken.idToken) {
                store.commit("setIdToken", newToken.idToken);
            }
        });

        oktaAuth.tokenManager.on("error", (err) => {
            console.error("Okta token renewal error:", err);
        });
    }

    async logout() {
        this.isRefreshing = false;
        this.failedQueue = [];
        store.commit("clearTokens");
    }

    isAuthenticated() {
        return !!store.state.access_token;
    }

    getCurrentUser() {
        const idToken = store.state.id_token;
        if (!idToken) return null;

        try {
            const payload = JSON.parse(atob(idToken.split(".")[1]));
            return {
                username: payload.preferred_username || payload.email,
                email: payload.email,
                sub: payload.sub,
                emailVerified: payload.email_verified,
                name: payload.name,
                groups: payload.groups || []
            };
        } catch (error) {
            console.error("Error parsing ID token:", error);
            return null;
        }
    }

    getGroups() {
        const accessToken = store.state.access_token;
        if (!accessToken) return [];

        try {
            const payload = JSON.parse(atob(accessToken.split(".")[1]));
            return payload.groups || [];
        } catch (error) {
            console.error("Error parsing access token for groups:", error);
            return [];
        }
    }

    hasGroup(groupName) {
        const groups = this.getGroups();
        return groups.includes(groupName);
    }

    hasAnyGroup(groupNames) {
        const groups = this.getGroups();
        return groupNames.some(group => groups.includes(group));
    }

    getAccessToken() {
        return store.state.access_token;
    }

    isTokenExpired(token) {
        if (!token) return true;

        try {
            const payload = JSON.parse(atob(token.split(".")[1]));
            const now = Math.floor(Date.now() / 1000);
            return payload.exp < now;
        } catch (error) {
            console.error("Error parsing token:", error);
            return true;
        }
    }

    // Refresh tokens via Okta SDK (not the backend).
    // renew() returns the TokenObject directly (like the "renewed" event), not the container.
    async performTokenRefresh() {
        if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
                this.failedQueue.push({ resolve, reject });
            });
        }

        this.isRefreshing = true;

        try {
            const freshTokens = await oktaAuth.tokenManager.renew("accessToken");
            await oktaAuth.tokenManager.renew("idToken");

            const accessToken = freshTokens.accessToken;
            store.commit("setAccessToken", accessToken);

            const idTokens = oktaAuth.tokenManager.getTokensSync();
            if (idTokens.idToken) {
                store.commit("setIdToken", idTokens.idToken.idToken);
            }

            this.processQueue(null, accessToken);
            return accessToken;
        } catch (error) {
            console.error("Okta token refresh failed:", error);
            this.processQueue(error, null);
            store.commit("clearTokens");

            if (window.location.pathname !== "/login") {
                window.location.href = "/login";
            }
            throw error;
        } finally {
            this.isRefreshing = false;
        }
    }
}

const authService = new AuthService();

export default authService;
